/**
 * Fuzzy town name matching for "did you mean?" suggestions.
 * Uses Levenshtein distance to find the closest Essex town.
 */

const ESSEX_TOWNS = [
  'Basildon', 'Billericay', 'Braintree', 'Brentwood', 'Brightlingsea',
  'Burnham-on-Crouch', 'Canvey Island', 'Chelmsford', 'Clacton-on-Sea',
  'Coggeshall', 'Colchester', 'Corringham', 'Danbury', 'Dunmow',
  'Epping', 'Frinton-on-Sea', 'Grays', 'Halstead', 'Harlow',
  'Harwich', 'Hockley', 'Ingatestone', 'Leigh-on-Sea', 'Loughton',
  'Maldon', 'Manningtree', 'Mersea Island', 'Ongar', 'Rayleigh',
  'Rochford', 'Saffron Walden', 'South Woodham Ferrers', 'Southend-on-Sea',
  'Stanford-le-Hope', 'Stansted Mountfitchet', 'Tilbury', 'Tiptree',
  'Waltham Abbey', 'Walton-on-the-Naze', 'Wickford', 'Witham',
  'Writtle', 'Buckhurst Hill', 'Chigwell', 'Thaxted', 'Wivenhoe',
  'Southminster', 'Kelvedon', 'Hadleigh', 'Benfleet', 'Thundersley',
  'Great Baddow', 'Springfield', 'Stock', 'Galleywood', 'Hatfield Peverel'
];

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

/**
 * Given a search query, check if it looks like a misspelled Essex town name.
 * Returns { suggestion, original } or null if no close match found.
 */
export function suggestTown(query) {
  if (!query || query.length < 3) return null;
  const q = query.toLowerCase().trim();

  // Exact match — no suggestion needed
  if (ESSEX_TOWNS.some(t => t.toLowerCase() === q)) return null;

  let best = null;
  let bestDist = Infinity;

  for (const town of ESSEX_TOWNS) {
    const t = town.toLowerCase();
    // Only compare if lengths are somewhat similar
    if (Math.abs(t.length - q.length) > 3) continue;
    const dist = levenshtein(q, t);
    // Threshold: max 2 edits for short names, 3 for longer
    const threshold = q.length <= 6 ? 2 : 3;
    if (dist <= threshold && dist < bestDist) {
      bestDist = dist;
      best = town;
    }
  }

  return best ? { suggestion: best, original: query } : null;
}

/**
 * Check multiple words in a query for potential town misspellings.
 */
export function suggestFromQuery(query) {
  if (!query) return null;
  // First check the whole query
  const whole = suggestTown(query);
  if (whole) return whole;
  // Then check individual words
  const words = query.split(/\s+/);
  for (const word of words) {
    const result = suggestTown(word);
    if (result) return { ...result, fullQuery: query.replace(new RegExp(word, 'i'), result.suggestion) };
  }
  return null;
}

export { ESSEX_TOWNS };
